import React from "react";
import {
  Card,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import moment from "moment";
import { CopyableId } from "../../../utils/SensorNameMappings";

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 12,
    overflow: "hidden",
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: theme.spacing(3),
    background: "linear-gradient(180deg, #e8f4f8 0%, #d9eef3 100%)",
    borderBottom: "4px solid #0076bb",
  },
  title: {
    color: "#0076bb",
    fontWeight: 700,
    lineHeight: 1.2,
  },
  subtitle: {
    marginTop: theme.spacing(0.75),
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
  },
  chip: {
    backgroundColor: "#fff",
    fontWeight: 600,
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  successChip: {
    color: "#2e7d32",
    borderColor: "#2e7d32",
  },
  errorChip: {
    color: "#d32f2f",
    borderColor: "#d32f2f",
  },
  primaryChip: {
    color: "#0076bb",
    borderColor: "#0076bb",
  },
  lastUpdatedContainer: {
    padding: theme.spacing(2, 3),
    backgroundColor: "#d9eef3",
    borderTop: "1px solid rgba(0,0,0,0.08)",
  },
  lastUpdated: {
    color: "#009444",
    fontWeight: 600,
  },
  content: {
    padding: theme.spacing(3),
    flex: 1,
  },
  sectionTitle: {
    color: "#0076bb",
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: 1,
    textTransform: "uppercase",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    backgroundColor: "#fafafa",
  },
  detailLabel: {
    color: "#0076bb",
    fontWeight: 500,
  },
  copyButton: {
    color: "#0076bb",
    "&:hover": {
      backgroundColor: "#d9eef3",
    },
  },
  divider: {
    margin: theme.spacing(0.5, 0),
  },
}));

/* -------------------- ROW -------------------- */

function DetailRow({ heading, label, copyValue }) {
  const classes = useStyles();

  const handleCopy = async () => {
    if (!copyValue) return;
    await navigator.clipboard.writeText(String(copyValue));
  };

  return (
    <Box className={classes.detailRow}>
      <Typography variant="body2" className={classes.detailLabel}>
        {label}
      </Typography>

      {copyValue && (
        <Tooltip title={`Copy ${heading}`}>
          <IconButton
            size="small"
            onClick={handleCopy}
            className={classes.copyButton}
          >
            <FileCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

const formatLastReportedOn = (value) => {
  if (!value) {
    return "N/A";
  }
  return moment(new Date(value)).format("MM/DD/YYYY hh:mm A");
};

function SectionTitle({ children }) {
  const classes = useStyles();
  return <Typography className={classes.sectionTitle}>{children}</Typography>;
}

export default function SensorDetailsCard({ data }) {
  const classes = useStyles();

  const sensor = data?.sensor;
  const identifiers = data?.identifiers ?? [];
  const facility = data?.facility;

  if (!sensor) return null;

  return (
    <Card elevation={6} className={classes.card}>
      <Box className={classes.header}>
        <Box>
          <Typography variant="h5" className={classes.title}>
            {sensor.display_name || sensor.name}
          </Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.subtitle}
          >
            <CopyableId label="ID" id={sensor.id} icon />
          </Typography>
        </Box>

        <Box className={classes.chipContainer}>
          <Chip
            label={sensor.isActive ? "Active" : "Inactive"}
            size="small"
            variant="outlined"
            className={`${classes.chip} ${
              sensor.isActive ? classes.successChip : classes.errorChip
            }`}
          />

          <Chip
            label={sensor.isHmacEnabled ? "HMAC Enabled" : "HMAC Disabled"}
            size="small"
            variant="outlined"
            className={`${classes.chip} ${
              sensor.isHmacEnabled ? classes.successChip : classes.errorChip
            }`}
          />

          {sensor.facility_id && (
            <Chip
              label={`Assigned to: ${facility?.park_name}`}
              size="small"
              variant="outlined"
              className={`${classes.chip} ${classes.primaryChip}`}
            />
          )}
        </Box>
      </Box>

      <Box className={classes.lastUpdatedContainer}>
        <Typography variant="body2" className={classes.lastUpdated}>
          Last Updated: {formatLastReportedOn(sensor.last_updated_on)}
        </Typography>
      </Box>

      <Box className={classes.content}>
        {identifiers.length ? (
          identifiers.map((item, index) => (
            <Box key={item.key || index}>
              <SectionTitle>{item.key}</SectionTitle>

              <DetailRow
                heading={item.key}
                label={item.value}
                copyValue={item.value}
              />

              {index !== identifiers.length - 1 && (
                <Divider className={classes.divider} />
              )}
            </Box>
          ))
        ) : (
          <DetailRow
            heading="Sensor Identifiers"
            label="No Information Available"
          />
        )}
      </Box>
    </Card>
  );
}
